import { useState, useEffect, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Stack, TextField, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const currentYear = new Date().getFullYear();
// Descending: currentYear → currentYear-120 (e.g. 2026 → 1906)
const YEARS = Array.from({ length: 121 }, (_, i) => currentYear - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export default function RHFBirthdayPicker({ name, label, required, disabled }) {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <BirthdaySelects
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label={label}
                    required={required}
                    disabled={disabled}
                    error={error}
                />
            )}
        />
    );
}

function BirthdaySelects({ value, onChange, onBlur, label, required, disabled, error }) {
    const { t } = useTranslation();
    const displayLabel = label || t('form_fields.birthday.label');
    const [day, setDay] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    // Sync from form value on mount / external changes.
    // Handles both Date objects and ISO date strings from API defaults.
    useEffect(() => {
        const d = value instanceof Date ? value : (value ? new Date(value) : null);
        if (d && !isNaN(d)) {
            setDay(d.getDate());
            setMonth(d.getMonth() + 1);
            setYear(d.getFullYear());
        } else {
            setDay('');
            setMonth('');
            setYear('');
        }
    }, [value]);

    const maxDay = useMemo(() => {
        if (month && year) return new Date(year, month, 0).getDate();
        if (month) return new Date(2000, month, 0).getDate();
        return 31;
    }, [month, year]);

    const buildDate = (d, m, y) => {
        if (!d || !m || !y) {
            if (value) onChange(null);
            return;
        }
        const date = new Date(y, m - 1, d, 12, 0, 0, 0);
        if (date.getDate() !== d || date.getMonth() !== m - 1) return;
        // Disallow future dates for a birthday field (calendar-day compare)
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        if (date > endOfToday) {
            onChange(null);
            return;
        }
        onChange(date);
    };

    const handleDay = (e) => {
        const v = e.target.value;
        setDay(v);
        buildDate(v, month, year);
    };

    const handleMonth = (e) => {
        const v = e.target.value;
        setMonth(v);
        // Reset day if it exceeds new month's max
        const newMax = new Date(year || 2000, v, 0).getDate();
        const newDay = day > newMax ? '' : day;
        if (day > newMax) setDay('');
        buildDate(newDay, v, year);
    };

    const handleYear = (e) => {
        const v = e.target.value;
        setYear(v);
        const newMax = month ? new Date(v, month, 0).getDate() : 31;
        const newDay = Number(day) > newMax ? '' : day;
        if (Number(day) > newMax) setDay('');
        buildDate(newDay, month, v);
    };

    return (
        <Stack spacing={1} sx={{ width: '100%' }}>
            <Typography variant="body2" color="text.secondary">
                {displayLabel}{required ? '*' : ''}
            </Typography>
            <Stack direction="row" spacing={1}>
                <TextField
                    select
                    value={day}
                    onChange={handleDay}
                    onBlur={onBlur}
                    label={t('form_fields.birthday.day')}
                    disabled={disabled}
                    error={!!error}
                    size="small"
                    sx={{ flex: 1 }}
                >
                    {DAYS.filter((d) => d <= maxDay).map((d) => (
                        <MenuItem key={d} value={d}>{String(d).padStart(2, '0')}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    value={month}
                    onChange={handleMonth}
                    onBlur={onBlur}
                    label={t('form_fields.birthday.month')}
                    disabled={disabled}
                    error={!!error}
                    size="small"
                    sx={{ flex: 1 }}
                >
                    {MONTHS.map((m) => (
                        <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    value={year}
                    onChange={handleYear}
                    onBlur={onBlur}
                    label={t('form_fields.birthday.year')}
                    disabled={disabled}
                    error={!!error}
                    size="small"
                    sx={{ flex: 1 }}
                >
                    {YEARS.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                </TextField>
            </Stack>
            {error && (
                <Typography variant="caption" color="error">
                    {error.message}
                </Typography>
            )}
        </Stack>
    );
}
